import React from 'react';
import { ConnectorObject } from '../types';
import { intToHex } from '../utils';

interface ConnectorArrowProps {
    obj: ConnectorObject;
    isSelected: boolean;
    isPreview?: boolean;
    onEndpointDrag?: (e: React.MouseEvent, objectId: string, endpoint: 'start' | 'end' | 'breakpoint') => void;
    onMouseDown?: (e: React.MouseEvent, objectId: string) => void;
}

export const ConnectorArrow: React.FC<ConnectorArrowProps> = ({
    obj,
    isSelected,
    isPreview = false,
    onEndpointDrag,
    onMouseDown,
}) => {
    const absStartX = obj.position?.x ?? 0;
    const absStartY = obj.position?.y ?? 0;
    const absEndX = obj.endPosition?.x ?? (absStartX + (obj.size?.width ?? 100));
    const absEndY = obj.endPosition?.y ?? (absStartY + (obj.size?.height ?? 100));
    const startX = 0;
    const startY = 0;
    const endX = absEndX - absStartX;
    const endY = absEndY - absStartY;

    const lineWidth = obj.strokeWidth ?? 2;
    const arrowType = obj.arrowType ?? 'single';
    const lineStyle = obj.lineStyle ?? 'solid';
    const arrowStyle = obj.arrowStyle ?? 'straight';
    const color = intToHex(obj.color);
    const arrowHeadSize = obj.arrowHeadSize ?? 1;
    const arrowHeadColor = intToHex(obj.arrowHeadColor ?? obj.color);
    const hasBreakpoint = obj.hasBreakpoint ?? false;

    const absBreakpointX = obj.breakpointX ?? (absStartX + absEndX) / 2;
    const absBreakpointY = obj.breakpointY ?? (absStartY + absEndY) / 2;
    const breakpointX = absBreakpointX - absStartX;
    const breakpointY = absBreakpointY - absStartY;

    // Calculate actual bounds for the SVG box
    const padding = 40 * arrowHeadSize;
    const minX_raw = Math.min(startX, endX, hasBreakpoint ? breakpointX : endX);
    const minY_raw = Math.min(startY, endY, hasBreakpoint ? breakpointY : endY);
    const maxX_raw = Math.max(startX, endX, hasBreakpoint ? breakpointX : endX);
    const maxY_raw = Math.max(startY, endY, hasBreakpoint ? breakpointY : endY);

    const minX = minX_raw - padding;
    const minY = minY_raw - padding;
    const maxX = maxX_raw + padding;
    const maxY = maxY_raw + padding;

    const svgWidth = maxX - minX;
    const svgHeight = maxY - minY;

    const markerId = isPreview ? `connector-end-${obj.id}-preview` : `connector-end-${obj.id}`;
    const markerIdStart = isPreview ? `connector-start-${obj.id}-preview` : `connector-start-${obj.id}`;

    const renderMarker = (id: string, isStart: boolean) => {
        const baseSize = 10;
        const size = baseSize * arrowHeadSize;
        return (
            <marker
                id={id}
                markerWidth={size}
                markerHeight={size}
                refX={isStart ? size : 0}
                refY={size / 2}
                orient="auto"
                markerUnits="userSpaceOnUse"
            >
                <polygon
                    points={
                        isStart
                            ? `${size} 0, 0 ${size / 2}, ${size} ${size}`
                            : `0 0, ${size} ${size / 2}, 0 ${size}`
                    }
                    fill={arrowHeadColor}
                />
            </marker>
        );
    };
    const getPath = () => {
        if (hasBreakpoint) {
            if (arrowStyle === 'curve') {
                return `M ${startX} ${startY} Q ${breakpointX} ${breakpointY} ${endX} ${endY}`;
            }
            return `M ${startX} ${startY} L ${breakpointX} ${breakpointY} L ${endX} ${endY}`;
        }

        if (arrowStyle === 'curve') {
            const ctrlX1 = startX + (endX - startX) / 3;
            const ctrlY1 = startY;
            const ctrlX2 = endX - (endX - startX) / 3;
            const ctrlY2 = endY;
            return `M ${startX} ${startY} C ${ctrlX1} ${ctrlY1}, ${ctrlX2} ${ctrlY2}, ${endX} ${endY}`;
        } else if (arrowStyle === 'elbow') {
            const midX = startX + (endX - startX) / 2;
            return `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
        }
        return `M ${startX} ${startY} L ${endX} ${endY}`;
    };

    const getStrokeDashArray = () => {
        if (lineStyle === 'dashed') return '8,4';
        if (lineStyle === 'dotted') return '2,2';
        return 'none';
    };

    return (
        <svg
            width={svgWidth}
            height={svgHeight}
            viewBox={`${minX} ${minY} ${svgWidth} ${svgHeight}`}
            style={{
                position: 'absolute',
                left: (obj.position?.x ?? 0) + minX,
                top: (obj.position?.y ?? 0) + minY,
                pointerEvents: 'none',
                zIndex: obj.zIndex ?? 0,
                transform: `rotate(${obj.rotation || 0}deg)`,
                transformOrigin: `${-minX}px ${-minY}px`,
            }}
        >
            {/* Markers removed for html2canvas compatibility - arrowheads are now inline elements */}
            <path
                d={getPath()}
                stroke={color}
                strokeWidth={lineWidth}
                strokeDasharray={getStrokeDashArray()}
                fill="none"
                style={{ pointerEvents: 'stroke', cursor: 'move' }}
                onMouseDown={(e) => {
                    if (!isPreview && onMouseDown) {
                        onMouseDown(e, obj.id);
                    }
                }}
            />

            {/* End arrowhead rendered as a manual element for capture compatibility */}
            <polygon
                points={(() => {
                    // Calculate angle based on the end of the line
                    let prevX = startX, prevY = startY;
                    if (hasBreakpoint) {
                        prevX = breakpointX; prevY = breakpointY;
                    } else if (arrowStyle === 'elbow') {
                        prevX = startX + (endX - startX) / 2;
                        prevY = endY;
                    } else if (arrowStyle === 'curve') {
                        // Approximate tangent at end of curve
                        prevX = endX - (endX - startX) / 10;
                        prevY = endY - (endY - startY) / 10;
                    }
                    const angle = Math.atan2(endY - prevY, endX - prevX);

                    const headLen = 10 * arrowHeadSize;
                    const headWidth = 5 * arrowHeadSize;

                    // The tip of the arrow is at (endX, endY)
                    const x0 = endX;
                    const y0 = endY;

                    // Calculate the two base points of the triangle
                    const x1 = endX - headLen * Math.cos(angle) + headWidth * Math.sin(angle);
                    const y1 = endY - headLen * Math.sin(angle) - headWidth * Math.cos(angle);
                    const x2 = endX - headLen * Math.cos(angle) - headWidth * Math.sin(angle);
                    const y2 = endY - headLen * Math.sin(angle) + headWidth * Math.cos(angle);

                    return `${x0},${y0} ${x1},${y1} ${x2},${y2}`;
                })()}
                fill={arrowHeadColor}
            />

            {/* Start arrowhead if double-headed */}
            {arrowType === 'double' && (
                <polygon
                    points={(() => {
                        // Calculate angle based on the start of the line
                        let nextX = endX, nextY = endY;
                        if (hasBreakpoint) {
                            nextX = breakpointX; nextY = breakpointY;
                        } else if (arrowStyle === 'elbow') {
                            nextX = startX + (endX - startX) / 2;
                            nextY = startY;
                        } else if (arrowStyle === 'curve') {
                            nextX = startX + (endX - startX) / 10;
                            nextY = startY + (endY - startY) / 10;
                        }
                        const angle = Math.atan2(startY - nextY, startX - nextX);

                        const headLen = 10 * arrowHeadSize;
                        const headWidth = 5 * arrowHeadSize;

                        // The tip of the arrow is at (startX, startY)
                        const x0 = startX;
                        const y0 = startY;

                        // Calculate the two base points of the triangle
                        const x1 = startX - headLen * Math.cos(angle) + headWidth * Math.sin(angle);
                        const y1 = startY - headLen * Math.sin(angle) - headWidth * Math.cos(angle);
                        const x2 = startX - headLen * Math.cos(angle) - headWidth * Math.sin(angle);
                        const y2 = startY - headLen * Math.sin(angle) + headWidth * Math.cos(angle);

                        return `${x0},${y0} ${x1},${y1} ${x2},${y2}`;
                    })()}
                    fill={arrowHeadColor}
                />
            )}
            {isSelected && !isPreview && (
                <>
                    {/* Start endpoint handle */}
                    <circle
                        cx={startX}
                        cy={startY}
                        r={6}
                        fill="#1890ff"
                        stroke="white"
                        strokeWidth={2}
                        style={{ cursor: 'move', pointerEvents: 'all' }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            onEndpointDrag?.(e, obj.id, 'start');
                        }}
                    />
                    {/* End endpoint handle */}
                    <circle
                        cx={endX}
                        cy={endY}
                        r={6}
                        fill="#1890ff"
                        stroke="white"
                        strokeWidth={2}
                        style={{ cursor: 'move', pointerEvents: 'all' }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            onEndpointDrag?.(e, obj.id, 'end');
                        }}
                    />
                    {/* Breakpoint handle */}
                    {hasBreakpoint && (
                        <circle
                            cx={breakpointX}
                            cy={breakpointY}
                            r={6}
                            fill="#52c41a"
                            stroke="white"
                            strokeWidth={2}
                            style={{ cursor: 'move', pointerEvents: 'all' }}
                            onMouseDown={(e) => {
                                e.stopPropagation();
                                onEndpointDrag?.(e, obj.id, 'breakpoint' as any);
                            }}
                        />
                    )}
                </>
            )}
        </svg>
    );
};
